import React, { Component } from "react";
import { Card, CardBody, CardHeader, Button, Input } from "reactstrap";
import DateRangePicker from "react-dates/lib/components/DateRangePicker";
import "react-datepicker/dist/react-datepicker.css";
import { BootstrapTable, TableHeaderColumn } from "react-bootstrap-table";
import moment from "moment-timezone";
import { APP_CONST, getStoredItem } from "@/store/dataStore";
import LoadingOverlay from "@/views/Components/LoadingOverlay";
import MultipleSelect from "@/views/Components/MultipleSelect";
import logs from "@/api/logs";
import { handleError } from "@/util/errorHandler";

class AuditLogs extends Component {
  state = {
    roleOptions: [],
    activityOptions: [],
    selectedStatus: "",
    from: moment(),
    to: moment(),
    filter: "",
    contents: [],
    expandedRowsIds: [],
    pagination: {
      hasNext: false,
      hasPrevious: false,
      size: 10,
      number: 0,
    },
    user_permissions: JSON.parse(getStoredItem(APP_CONST.USER_PERMISSIONS)),
    user_authorities: JSON.parse(getStoredItem(APP_CONST.USER_AUTHORITIES)),
    refresh: true,
    loading: false,
  };

  interval = null;
  roles = [];
  activities = [];

  bindInnerTableRef = innerComponent => {
    this.table = innerComponent;
  };

  onSearch = () => {
    this.loadPagination(0);
  };

  onSelectDate = date => {
    const currentYear = new Date().getFullYear();
    const year =
      new Date(date).getFullYear() > currentYear
        ? currentYear
        : new Date(date).getFullYear();
    const newDate = new Date(date).setFullYear(year);

    this.setState({
      generateDateTime: newDate,
    });
  };

  handleStatusChange = event => {
    this.setState({
      selectedStatus: event.target.value,
    });
  };

  loadPagination = async (
    page = this.state.pagination.number,
    size = this.state.pagination.size,
    sort = "logDateTime,desc",
  ) => {
    this.setState({ loading: true });
    const dateFrom = this.state.from
      ? this.state.from.format("YYYY-MM-DD")
      : "";
    const dateTo = this.state.to ? this.state.to.format("YYYY-MM-DD") : "";
    const res = await logs
      .listAuditLogs({
        page,
        size,
        filter: this.state.filter,
        sort: sort,
        dateFrom: dateFrom,
        dateTo: dateTo,
        roles: this.roles,
        activities: this.activities,
      })
      .catch(handleError)
      .finally(() => {
        this.setState({ loading: false });
      });
    this.setState({
      contents: res.data.content,
      pagination: res.data.page,
      loading: false,
    });
  };

  toggleRefresh = () => {
    this.setState({ refresh: !this.state.refresh });
  };

  toggleParams = row => {
    const { expandedRowsIds } = this.state;
    const index = expandedRowsIds.indexOf(row.id);
    if (index > -1) {
      expandedRowsIds.splice(index, 1);
    } else {
      expandedRowsIds.push(row.id);
    }
    this.setState({ expandedRowsIds });
  };

  // Render functions to render fields in the table ========================================

  renderField = (cell, row) => {
    const field = this.state.expandedRowsIds.includes(row.id) ? (
      <pre
        style={{
          overflow: "hidden",
          fontFamily: "inherit",
          fontSize: "inherit",
          whiteSpace: "normal",
        }}>
        {cell}
      </pre>
    ) : (
      <p
        style={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}>
        {cell}
      </p>
    );
    return field;
  };

  renderExpandable = (cell, row) => {
    const icon = this.state.expandedRowsIds.includes(row.id) ? (
      <i
        className="fa fa-chevron-up"
        style={{ cursor: "pointer", marginRight: 5 }}
        onClick={() => this.toggleParams(row)}
      />
    ) : (
      <i
        className="fa fa-chevron-down"
        style={{ cursor: "pointer", marginRight: 5 }}
        onClick={() => this.toggleParams(row)}
      />
    );
    return (
      <div style={{ display: "flex", justifyContent: "center" }}>{icon}</div>
    );
  };

  renderRoles = (cell, row) => {
    const { roles } = row;
    const roleStr = roles.map(role => role).join("\n");
    return this.renderField(roleStr, row);
  };

  renderFullName = (cell, row) => {
    const { fullName } = row;
    return this.renderField(fullName, row);
  };

  renderUsername = (cell, row) => {
    const { username } = row;
    return this.renderField(username, row);
  };

  renderActivity = (cell, row) => {
    const { activity } = row;
    return this.renderField(activity, row);
  };

  renderDescription = (cell, row) => {
    const { description } = row;
    return this.renderField(description, row);
  };

  renderLogDateTime = (cell, row) => {
    const { logDateTime } = row;
    const date = new Date(logDateTime * 1000);

    const formattedDate = date.toISOString().split("T")[0];
    const formattedTime = date.toTimeString().split(" ")[0];

    const result = `${formattedDate} ${formattedTime}`;
    return this.renderField(result, row);
  };

  renderMessage = (cell, row) => {
    const { message } = row;
    return this.renderField(message, row);
  };

  // ========================================================================================

  onPrevious = () => {
    const { pagination } = this.state;
    const prevPage = pagination.number - 1;
    this.loadPagination(prevPage);
  };

  onNext = () => {
    const { pagination } = this.state;
    const nextPage = pagination.number + 1;
    this.loadPagination(nextPage);
  };

  onPageChange = (page, sizePerPage) => {
    this.loadPagination(page, sizePerPage);
  };

  onSortChange = (sortName, sortOrder) => {
    const sort = `${sortName},${sortOrder}`;
    this.loadPagination(0, this.state.pagination.size, sort);
  };

  componentDidMount() {
    this.loadPagination();

    // get options
    logs.getRolesOptions().then(data => {
      const options = [];
      data.map(role =>
        options.push({
          label: role,
          value: role,
        }),
      );
      this.setState({ roleOptions: options });
    });

    logs.getActivityOptions().then(data => {
      const options = [];
      data.map(activity =>
        options.push({
          label: activity,
          value: activity,
        }),
      );
      this.setState({ activityOptions: options });
    });
  }

  render() {
    return (
      <Card>
        <CardHeader>Audit Log</CardHeader>
        <div
          className="pt-3 px-4"
          style={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
          }}>
          <div
            className="col-12"
            style={{ display: "flex", justifyContent: "flex-end" }}>
            <div className="col-2">
              <MultipleSelect
                options={this.state.roleOptions}
                placeholder={"Role Name"}
                onChange={values => {
                  this.roles =
                    values.length > 0
                      ? values.map(value => value.value).join(",")
                      : "";
                }}
              />
            </div>
            <div className="col-2">
              <MultipleSelect
                options={this.state.activityOptions}
                placeholder={"Activity"}
                onChange={values => {
                  this.activities =
                    values.length > 0
                      ? values.map(value => value.value).join(",")
                      : "";
                }}
              />
            </div>
            <div className="col-auto" style={{ justifyItems: "end" }}>
              <DateRangePicker
                startDate={this.state.from}
                startDateId="date_from"
                endDate={this.state.to}
                endDateId="date_to"
                onDatesChange={({ startDate, endDate }) =>
                  this.setState({ from: startDate, to: endDate })
                }
                focusedInput={this.state.focusedInput}
                onFocusChange={focusedInput => this.setState({ focusedInput })}
                showClearDates
                showDefaultInputIcon
                isOutsideRange={date => false}
                small
                minimumNights={0}
              />
            </div>
            <div className="col-3">
              <Input
                style={{ paddingRight: 100, height: 39 }}
                type="text"
                id="filter"
                placeholder="Search"
                onChange={e => this.setState({ filter: e.target.value })}
                onKeyDown={this.onEnterSearch}
                value={this.state.filter}
              />
            </div>
            <div className="col-auto">
              <Button
                className="p-0"
                color="primary"
                style={{
                  width: 100,
                  height: 35,
                }}
                onClick={this.onSearch}
                data-cy="submit">
                Search
              </Button>
            </div>
          </div>
        </div>
        <CardBody>
          <LoadingOverlay loading={this.state.loading} />
          <BootstrapTable
            version="4"
            striped
            hover
            condensed
            remote
            pagination={true}
            data={this.state.contents}
            options={{
              sizePerPage: this.state.pagination.size,
              sizePerPageList: [
                {
                  text: "5",
                  value: 5,
                },
                {
                  text: "10",
                  value: 10,
                },
                {
                  text: "25",
                  value: 25,
                },
                {
                  text: "50",
                  value: 50,
                },
              ],
              onPageChange: this.onPageChange,
              onSortChange: this.onSortChange,
            }}
            keyField="id">
            <TableHeaderColumn
              width="30"
              dataFormat={this.renderExpandable}></TableHeaderColumn>
            <TableHeaderColumn
              dataSort
              dataField="fullName"
              dataAlign="center"
              dataFormat={this.renderFullName}
              width="150">
              Full Name
            </TableHeaderColumn>
            <TableHeaderColumn
              dataSort
              dataField="username"
              dataFormat={this.renderUsername}
              width="150">
              Username
            </TableHeaderColumn>
            <TableHeaderColumn
              dataField="username"
              dataFormat={this.renderRoles}
              width="160">
              Role Name
            </TableHeaderColumn>
            <TableHeaderColumn
              dataSort
              dataField="activity"
              dataFormat={this.renderActivity}
              width="100">
              Activity
            </TableHeaderColumn>
            <TableHeaderColumn
              dataSort
              dataField="description"
              dataFormat={this.renderDescription}
              width="300">
              Description
            </TableHeaderColumn>
            <TableHeaderColumn
              dataSort
              dataField="logDateTime"
              dataFormat={this.renderLogDateTime}
              width="120">
              Date Time
            </TableHeaderColumn>
            <TableHeaderColumn
              dataSort
              dataField="message"
              dataFormat={this.renderMessage}
              width="300">
              Message
            </TableHeaderColumn>
          </BootstrapTable>
          <div style={{ marginTop: 10, textAlign: "right" }}>
            <Button
              style={{ width: 100 }}
              onClick={this.onPrevious}
              disabled={this.state.pagination.hasPrevious === false}
              data-cy="back">
              <i className="fa fa-chevron-left" style={{ marginRight: 5 }} />
              Prev
            </Button>
            <Button
              color="primary"
              style={{ marginLeft: 20, width: 100 }}
              onClick={this.onNext}
              disabled={this.state.pagination.hasNext === false}
              data-cy="submit">
              Next
              <i className="fa fa-chevron-right" style={{ marginLeft: 5 }} />
            </Button>
          </div>
        </CardBody>
      </Card>
    );
  }
}

export default AuditLogs;
